import SearchDataGridWithTotalRow from 'components/search/search-data-grid/search-data-grid-with-total-row/search-data-grid-with-total-row.component';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LoyaltyDataGridToolbar from '../loyalty-data-grid-toolbar/loyalty-data-grid-toolbar.component';
import {
  useExportLocationsPerformanceCSVMutation,
  useExportOfferPerformanceCSVMutation,
} from 'api/reports-service/reports-service';
import { useSearchParamsContext } from 'components/search/search.hooks';
import { getFileName } from '../loyalty-performance-report.utils';
import OverPercentageTooltipCellRenderer from '../over-percentage-tooltip-cell-renderer/over-percentage-tooltip-cell-renderer.component';
import PerformanceDataGridDateRange from './performance-data-grid-date-range/performance-data-grid-date-range.component';

type PerformanceDataGridProps = {
  'data-testid': string;
  queryName: string;
  displayInfoHeaderName: string;
  isOffer?: boolean;
};

const PerformanceDataGrid = ({
  displayInfoHeaderName,
  queryName,
  isOffer = false,
  ...rest
}: PerformanceDataGridProps) => {
  const { t } = useTranslation();
  const { searchParams } = useSearchParamsContext();

  const columnList = [
    {
      field: 'newMembers',
      headerName: t(`page.loyaltyPerformanceReport.newMembers.label`),
    },
    !isOffer && {
      field: 'membersWhoOrdered',
      headerName: t(`page.loyaltyPerformanceReport.membersWhoOrdered.label`),
    },
    isOffer && {
      field: 'offerType',
      headerName: t(`page.loyaltyPerformanceReport.offerType.label`),
    },
    isOffer && {
      field: 'distributionMethod',
      headerName: t(`page.loyaltyPerformanceReport.distributionMethod.label`),
    },
    isOffer && {
      field: 'distributionDateRange',
      headerName: t(
        `page.loyaltyPerformanceReport.distributionDateRange.label`,
      ),
      renderCell: params => {
        const {
          distributionDateRangeStart,
          distributionDateRangeEnd,
          distributionEndDateType,
        } = params.row;
        return (
          <PerformanceDataGridDateRange
            startDate={distributionDateRangeStart}
            endDate={distributionDateRangeEnd}
            type={distributionEndDateType}
          />
        );
      },
    },
    isOffer && {
      field: 'redemptionsSelectedTimePeriodDistributions',
      headerName: t(
        `page.loyaltyPerformanceReport.redemptionsSelectedTimePeriodDistributions.label`,
      ),
    },
    isOffer && {
      field: 'redemptionRateDistributedAllTimeRedeemedInPeriod',
      headerName: t(
        `page.loyaltyPerformanceReport.redemptionRateDistributedAllTimeRedeemedInPeriod.label`,
      ),
    },
    isOffer && {
      field: 'redemptionDateRange',
      headerName: t(`page.loyaltyPerformanceReport.redemptionDateRange.label`),
      renderCell: params => {
        const {
          redemptionDateRangeStart,
          redemptionDateRangeEnd,
          redemptionEndDateType,
          redemptionEndTime,
        } = params.row;
        return (
          <PerformanceDataGridDateRange
            startDate={redemptionDateRangeStart}
            endDate={redemptionDateRangeEnd}
            type={redemptionEndDateType}
            redemptionEndTime={redemptionEndTime}
          />
        );
      },
    },
    isOffer && {
      field: 'distributionsSelectedTimePeriod',
      headerName: t(
        `page.loyaltyPerformanceReport.distributionsSelectedTimePeriod.label`,
      ),
    },
    isOffer && {
      field: 'redemptionsAllTimeDistributions',
      headerName: t(
        `page.loyaltyPerformanceReport.redemptionsAllTimeDistributions.label`,
      ),
    },
    isOffer && {
      field: 'redemptionRateDistributedAndRedeemedInPeriod',
      headerName: t(
        `page.loyaltyPerformanceReport.redemptionRateDistributedAndRedeemedInPeriod.label`,
      ),
    },
    isOffer && {
      field: 'redemptionRateAllTime',
      headerName: t(
        `page.loyaltyPerformanceReport.redemptionRateAllTime.label`,
      ),
    },
    isOffer && {
      field: 'averageDaysToRedeem',
      headerName: t(`page.loyaltyPerformanceReport.averageDaysToRedeem.label`),
    },
    {
      field: 'loyaltyOrdersNetSales',
      headerName: isOffer
        ? t(`page.loyaltyPerformanceReport.ordersNetSales.label`)
        : t(`page.loyaltyPerformanceReport.loyaltyOrdersNetSales.label`),
    },
    isOffer && {
      field: 'currentStatus',
      headerName: t(`page.loyaltyPerformanceReport.currentStatus.label`),
    },
    !isOffer && {
      field: 'deletedAccounts',
      headerName: t(`page.loyaltyPerformanceReport.deletedAccounts.label`),
    },
    {
      field: 'activeMembers',
      headerName: t(`page.loyaltyPerformanceReport.activeMembers.label`),
    },
    !isOffer && {
      field: 'activeMemberPercent',
      headerName: t(`page.loyaltyPerformanceReport.activeMemberPercent.label`),
      renderCell: params => (
        <OverPercentageTooltipCellRenderer
          value={params.value}
          title={t(
            'page.loyaltyPerformanceReport.activeMemberPercent.gridTooltip.title',
          )}
          description={t(
            'page.loyaltyPerformanceReport.activeMemberPercent.gridTooltip.description',
          )}
          showTooltip={value => Number(value.replace('%', '')) > 100}
        />
      ),
    },
    !isOffer && {
      field: 'totalOrders',
      headerName: t(`page.loyaltyPerformanceReport.totalOrders.label`),
    },
    {
      field: 'totalLoyaltyOrders',
      headerName: t(`page.loyaltyPerformanceReport.totalLoyaltyOrders.label`),
    },
    {
      field: 'loyaltyEarnOrders',
      headerName: t(`page.loyaltyPerformanceReport.loyaltyEarnOrders.label`),
    },
    {
      field: 'loyaltyBurnOrders',
      headerName: t(`page.loyaltyPerformanceReport.loyaltyBurnOrders.label`),
    },
    {
      field: 'loyaltyNetZeroOrders',
      headerName: t(`page.loyaltyPerformanceReport.loyaltyNetZeroOrders.label`),
    },
    {
      field: 'bonusEarnEvents',
      headerName: t(`page.loyaltyPerformanceReport.bonusEarnEvents.label`),
    },
    {
      field: 'loyaltyContributionOrdersPercent',
      headerName: t(
        `page.loyaltyPerformanceReport.loyaltyContributionPercentOfOrders.label`,
      ),
    },
    {
      field: 'loyaltyContributionSalesPercent',
      headerName: t(
        `page.loyaltyPerformanceReport.loyaltyContributionPercentOfSales.label`,
      ),
    },
    {
      field: 'loyaltyAverageCheck',
      headerName: isOffer
        ? t(`page.loyaltyPerformanceReport.averageCheck.label`)
        : t(`page.loyaltyPerformanceReport.loyaltyAverageCheck.label`),
    },
    isOffer && {
      field: 'averageDiscount',
      headerName: t(
        `page.loyaltyPerformanceReport.averageDiscountPercent.label`,
      ),
    },
    {
      field: 'discountAmount',
      headerName: t(`page.loyaltyPerformanceReport.discountAmount.label`),
    },
    !isOffer && {
      field: 'allOrdersNetSales',
      headerName: t(`page.loyaltyPerformanceReport.allOrdersNetSales.label`),
    },
    {
      field: 'mobileLoyaltyOrders',
      headerName: !isOffer
        ? t(`page.loyaltyPerformanceReport.mobileLoyaltyOrders.label`)
        : t(`page.loyaltyPerformanceReport.mobileOrders.label`),
    },
    {
      field: 'mobileLoyaltyOrdersPercent',
      headerName: !isOffer
        ? t(`page.loyaltyPerformanceReport.mobileLoyaltyOrdersPercent.label`)
        : t(`page.loyaltyPerformanceReport.mobileOrdersPercent.label`),
    },
    {
      field: 'webLoyaltyOrders',
      headerName: !isOffer
        ? t(`page.loyaltyPerformanceReport.webLoyaltyOrders.label`)
        : t(`page.loyaltyPerformanceReport.webOrders.label`),
    },
    {
      field: 'webLoyaltyOrdersPercent',
      headerName: !isOffer
        ? t(`page.loyaltyPerformanceReport.webLoyaltyOrdersPercent.label`)
        : t(`page.loyaltyPerformanceReport.webOrdersPercent.label`),
    },
    {
      field: 'scan2PayOrders',
      headerName: !isOffer
        ? t(`page.loyaltyPerformanceReport.scanToPayLoyaltyOrders.label`)
        : t(`page.loyaltyPerformanceReport.scanToPayOrders.label`),
    },
    {
      field: 'scan2PayOrdersPercent',
      headerName: !isOffer
        ? t(`page.loyaltyPerformanceReport.scanToPayLoyaltyOrdersPercent.label`)
        : t(`page.loyaltyPerformanceReport.scanToPayOrdersPercent.label`),
    },
    {
      field: 'otherLoyaltyOrders',
      headerName: !isOffer
        ? t(`page.loyaltyPerformanceReport.otherLoyaltyOrders.label`)
        : t(`page.loyaltyPerformanceReport.otherOrders.label`),
    },
    {
      field: 'otherLoyaltyOrdersPercent',
      headerName: !isOffer
        ? t(`page.loyaltyPerformanceReport.otherLoyaltyOrdersPercent.label`)
        : t(`page.loyaltyPerformanceReport.otherOrdersPercent.label`),
    },
    {
      field: 'inStoreLoyaltyOrders',
      headerName: !isOffer
        ? t(`page.loyaltyPerformanceReport.inStoreLoyaltyOrders.label`)
        : t(`page.loyaltyPerformanceReport.inStoreOrders.label`),
    },
    {
      field: 'inStoreLoyaltyOrdersPercent',
      headerName: !isOffer
        ? t(`page.loyaltyPerformanceReport.inStoreLoyaltyOrdersPercent.label`)
        : t(`page.loyaltyPerformanceReport.inStoreOrdersPercent.label`),
    },
    {
      field: 'uniqueUserScan2Pay',
      headerName: t(`page.loyaltyPerformanceReport.uniqueUserScanToPay.label`),
    },

    isOffer && {
      field: 'totalOpened',
      headerName: t(`page.loyaltyPerformanceReport.totalOpened.label`),
    },
    isOffer && {
      field: 'totalDeleted',
      headerName: t(`page.loyaltyPerformanceReport.totalDeleted.label`),
    },
    isOffer && {
      field: 'totalSaved',
      headerName: t(`page.loyaltyPerformanceReport.totalSaved.label`),
    },
    {
      field: 'uniqueUserWeb',
      headerName: t(`page.loyaltyPerformanceReport.uniqueUserWeb.label`),
    },
    {
      field: 'uniqueUserApp',
      headerName: t(`page.loyaltyPerformanceReport.uniqueUserApp.label`),
    },
    {
      field: 'uniqueUserInStore',
      headerName: t(`page.loyaltyPerformanceReport.uniqueUserInStore.label`),
    },
    {
      field: 'uniqueUserOther',
      headerName: t(`page.loyaltyPerformanceReport.uniqueUserOther.label`),
    },
  ].filter(Boolean);

  const columns = useMemo(
    () => [
      {
        field: 'displayInfoTitle',
        headerName: displayInfoHeaderName,
        minWidth: 300,
        hideable: false,
        sortable: true,
      },
      ...columnList.map(fields => ({
        ...fields,
        flex: 1,
        minWidth: 150,
        hideable: true,
        sortable: true,
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const columnVisibilityModel = {
    activeMembers: !isOffer,
    loyaltyEarnOrders: !isOffer,
    loyaltyBurnOrders: !isOffer,
    averageDiscount: isOffer,
    loyaltyNetZeroOrders: false,
    newMembers: false,
    membersWhoOrdered: false,
    deletedAccounts: false,
    activeMemberPercent: false,
    totalOrders: false,
    bonusEarnEvents: false,
    totalLoyaltyOrders: false,
    loyaltyContributionOrdersPercent: false,
    loyaltyContributionSalesPercent: false,
    allOrdersNetSales: false,
    loyaltyOrdersNetSales: false,
    mobileLoyaltyOrders: false,
    mobileLoyaltyOrdersPercent: false,
    webLoyaltyOrders: false,
    webLoyaltyOrdersPercent: false,
    scan2PayOrders: false,
    scan2PayOrdersPercent: false,
    otherLoyaltyOrders: false,
    otherLoyaltyOrdersPercent: false,
    inStoreLoyaltyOrders: false,
    inStoreLoyaltyOrdersPercent: false,
    uniqueUserScan2Pay: false,
    uniqueUserWeb: false,
    uniqueUserApp: false,
    uniqueUserInStore: false,
    uniqueUserOther: false,
    distributionDateRange: false,
    redemptionsSelectedTimePeriodDistributions: false,
    totalOpened: false,
    totalDeleted: false,
    totalSaved: false,
  };

  return (
    <SearchDataGridWithTotalRow
      columns={columns}
      toolbar={{
        columns: {
          enabled: true,
        },
      }}
      initialState={{
        columns: {
          columnVisibilityModel,
        },
        pinnedColumns: { left: ['displayInfoTitle'] },
      }}
      slots={{
        toolbar: LoyaltyDataGridToolbar,
      }}
      slotProps={{
        toolbar: {
          toolbar: {
            export: {
              query: isOffer
                ? useExportOfferPerformanceCSVMutation
                : useExportLocationsPerformanceCSVMutation,
              fileName: getFileName(
                {
                  startDate: searchParams.startdate.value as string,
                  endDate: searchParams.enddate.value as string,
                },
                isOffer ? 'OFFER_PERFORMANCE' : 'LOCATION_PERFORMANCE',
              ),
              queryName,
              filteredSearchParams: [
                'granularity',
                'comparisonstartdate',
                'comparisonenddate',
                'count',
                'page',
                'datatype',
                'sortBy',
                !isOffer ? 'offerIds' : undefined,
                !isOffer ? 'offertype' : undefined,
                'offerdatatype',
                'sortDirection',
              ].filter(Boolean),
              includeColumnsField: true,
            },
          },
        },
      }}
      queryName={queryName}
      {...rest}
    />
  );
};

export default PerformanceDataGrid;
